import { Injectable } from '@angular/core';

// Raw data format: array of arrays
type RawSportsDataRecord = [
  number, // timestamp
  number, // altitude
  number, // cadence
  number, // distance
  number, // elapsed_time
  number, // heart_rate
  number, // position_lat
  number, // position_lon
  number, // power
  number, // speed
  number  // temperature
];

@Injectable({
  providedIn: 'root'
})
export class SportsDataInterpolationService {

  /**
   * Linear interpolation function equivalent to scipy.interpolate.interp1d
   */
  private createLinearInterpolator(xValues: number[], yValues: number[]) {
    return (x: number): number => {
      // Handle edge cases - extrapolate using first/last values
      if (x <= xValues[0]) return yValues[0];
      if (x >= xValues[xValues.length - 1]) return yValues[yValues.length - 1];

      // Find the interval containing x
      let i = 0;
      while (i < xValues.length - 1 && xValues[i + 1] < x) {
        i++;
      }

      // Linear interpolation formula: y = y0 + (y1 - y0) * (x - x0) / (x1 - x0)
      const x0 = xValues[i];
      const x1 = xValues[i + 1];
      const y0 = yValues[i];
      const y1 = yValues[i + 1];

      return y0 + (y1 - y0) * (x - x0) / (x1 - x0);
    };
  }

  /**
   * Process and interpolate sports data records to create uniform 1Hz time series
   * @param parsedRecords - Array of arrays in the format: [timestamp, altitude, cadence, distance, elapsed_time, heart_rate, position_lat, position_lon, power, speed, temperature]
   * @returns Array of arrays with interpolated data in the same format, with 1 record per second
   */
  processSportsData(parsedRecords: any): RawSportsDataRecord[] {
    if (!parsedRecords || parsedRecords.length === 0) {
      return [];
    }

    // Sort records by timestamp to ensure proper order
    const sortedRecords = [...parsedRecords].sort((a, b) => a[0] - b[0]);

    // Extract timestamps and find time range
    const timestamps = sortedRecords.map(record => record[0]);
    const minTimestamp = timestamps[0];
    const maxTimestamp = timestamps[timestamps.length - 1];

    // Create uniform time series (1Hz) - one record per second from min to max
    const uniformTimestamps: number[] = [];
    for (let t = minTimestamp; t <= maxTimestamp; t++) {
      uniformTimestamps.push(t);
    }

    // Convert timestamps to seconds from start for interpolation calculations
    const timeSecondsFromStart = timestamps.map(ts => ts - minTimestamp);
    const uniformTimeSecondsFromStart = uniformTimestamps.map(ts => ts - minTimestamp);

    // Create interpolation functions for each column
    const columnNames = ['timestamp', 'altitude', 'cadence', 'distance', 'elapsed_time',
      'heart_rate', 'position_lat', 'position_lon', 'power', 'speed', 'temperature'];

    const interpolators: { [key: string]: (x: number) => number } = {};

    columnNames.forEach((columnName, columnIndex) => {
      const yValues = sortedRecords.map(record => record[columnIndex]);
      interpolators[columnName] = this.createLinearInterpolator(timeSecondsFromStart, yValues);
    });

    // Generate interpolated data - return arrays in same format as input
    const interpolatedData: RawSportsDataRecord[] = uniformTimeSecondsFromStart.map(timeSecond => {
      return [
        interpolators['timestamp'](timeSecond),          // 0: timestamp
        interpolators['altitude'](timeSecond),           // 1: altitude
        interpolators['cadence'](timeSecond),            // 2: cadence
        interpolators['distance'](timeSecond),           // 3: distance
        interpolators['elapsed_time'](timeSecond),       // 4: elapsed_time
        interpolators['heart_rate'](timeSecond),         // 5: heart_rate
        interpolators['position_lat'](timeSecond),       // 6: position_lat
        interpolators['position_lon'](timeSecond),       // 7: position_lon
        interpolators['power'](timeSecond),              // 8: power
        interpolators['speed'](timeSecond),              // 9: speed
        interpolators['temperature'](timeSecond)         // 10: temperature
      ];
    });

    return interpolatedData;
  }

  /**
   * Get specific column data from interpolated results
   * @param data - Array of interpolated records
   * @param columnIndex - Index of the column (0=timestamp, 1=altitude, 2=cadence, etc.)
   */
  getColumnData(data: RawSportsDataRecord[], columnIndex: number): number[] {
    return data.map(record => record[columnIndex]);
  }

  /**
   * Get data statistics for a specific column
   * @param data - Array of interpolated records
   * @param columnIndex - Index of the column (0=timestamp, 1=altitude, etc.)
   */
  getDataStats(data: RawSportsDataRecord[], columnIndex: number): {
    min: number;
    max: number;
    mean: number;
    median: number;
    count: number;
  } {
    const values = this.getColumnData(data, columnIndex);
    const sortedValues = [...values].sort((a, b) => a - b);

    return {
      min: sortedValues[0],
      max: sortedValues[sortedValues.length - 1],
      mean: values.reduce((sum, val) => sum + val, 0) / values.length,
      median: sortedValues.length % 2 === 0
        ? (sortedValues[sortedValues.length / 2 - 1] + sortedValues[sortedValues.length / 2]) / 2
        : sortedValues[Math.floor(sortedValues.length / 2)],
      count: values.length
    };
  }

  /**
   * Filter data by time range
   * @param data - Array of interpolated records
   * @param startTimestamp - Start timestamp (Unix timestamp)
   * @param endTimestamp - End timestamp (Unix timestamp)
   */
  filterByTimeRange(data: RawSportsDataRecord[], startTimestamp: number, endTimestamp: number): RawSportsDataRecord[] {
    return data.filter(record =>
      record[0] >= startTimestamp && record[0] <= endTimestamp
    );
  }

  /**
   * Find gaps in the original data (gaps > 1 second)
   * @param data - Original raw data
   */
  findDataGaps(data: RawSportsDataRecord[]): Array<{start: number, end: number, duration: number}> {
    if (data.length < 2) return [];

    const sortedData = [...data].sort((a, b) => a[0] - b[0]);
    const gaps: Array<{start: number, end: number, duration: number}> = [];

    for (let i = 1; i < sortedData.length; i++) {
      const gap = sortedData[i][0] - sortedData[i-1][0];
      if (gap > 1) {
        gaps.push({
          start: sortedData[i-1][0],
          end: sortedData[i][0],
          duration: gap
        });
      }
    }

    return gaps;
  }

  /**
   * Get column names and their indices for reference
   */
  getColumnInfo(): Array<{index: number, name: string}> {
    return [
      {index: 0, name: 'timestamp'},
      {index: 1, name: 'altitude'},
      {index: 2, name: 'cadence'},
      {index: 3, name: 'distance'},
      {index: 4, name: 'elapsed_time'},
      {index: 5, name: 'heart_rate'},
      {index: 6, name: 'position_lat'},
      {index: 7, name: 'position_lon'},
      {index: 8, name: 'power'},
      {index: 9, name: 'speed'},
      {index: 10, name: 'temperature'}
    ];
  }
}
